import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { getPhaseViews, setStorageValue } from 'App/Helpers';
import { ConsentContext } from 'App/Providers/games/ConsentProvider';

import { canShowPushPhase } from './helpers';

/**
 * Adds PushPhase gating (show/views/total) on top of push state.
 * Mounted only when `PushProvider` is used with `enablePhase`.
 *
 * Passes the merged context value to `children` so the provider stays in
 * `PushProvider`.
 *
 * @param {Object} props
 * @param {string} props.storageKey
 * @param {{
 *   pushStatus: string,
 *   updatePushStatus: Function,
 *   isPushAvailable: boolean,
 *   pushServiceReady: boolean,
 *   setPushServiceReady: Function,
 * }} props.contextValue
 * @param {(contextValue: Object) => import('react').ReactNode} props.children
 */
const PushPhaseGate = ({ storageKey, contextValue, children }) => {
    const { functionalConsent } = useContext(ConsentContext);
    const { pushStatus, isPushAvailable } = contextValue;

    const [showPushPhase, setShowPushPhase] = useState(() =>
        isPushAvailable ? canShowPushPhase(storageKey) : false
    );
    const [pushPhaseViews, setPushPhaseViews] = useState(null);
    const [pushPromptTotal, setPushPromptTotal] = useState(null);

    useEffect(() => {
        if (!isPushAvailable) {
            setPushPhaseViews(0);
            return;
        }

        setPushPhaseViews(getPhaseViews(storageKey, 'push.phaseViews'));
    }, [isPushAvailable, storageKey]);

    useEffect(() => {
        if (typeof functionalConsent !== 'boolean' || !isPushAvailable) return;
        if (pushPromptTotal === null || pushPhaseViews === null) return;

        if (!functionalConsent) {
            setShowPushPhase(false);
            setStorageValue(storageKey, 'push.showPhase', false);
            return;
        }

        const curShowPushPhase =
            (pushStatus === 'default' || pushStatus === 'called') &&
            pushPhaseViews < pushPromptTotal;

        setShowPushPhase(curShowPushPhase);
        setStorageValue(storageKey, 'push.showPhase', curShowPushPhase);
    }, [
        pushPhaseViews,
        pushPromptTotal,
        functionalConsent,
        isPushAvailable,
        pushStatus,
        storageKey,
    ]);

    const updatePushPhaseViews = useCallback(() => {
        setPushPhaseViews((prevValue) => {
            const pushViews = prevValue + 1;

            setStorageValue(storageKey, 'push.phaseViews', pushViews);

            return pushViews;
        });
    }, [storageKey]);

    const phaseContextValue = useMemo(
        () => ({
            ...contextValue,
            showPushPhase,
            pushPhaseViews,
            setPushPromptTotal,
            updatePushPhaseViews,
        }),
        [contextValue, showPushPhase, pushPhaseViews, updatePushPhaseViews]
    );

    return children(phaseContextValue);
};

export { PushPhaseGate };
